/**
 * InlineCheckout — 3-step booking modal
 * Step 1: Date / Timeslot / Notes
 * Step 2: Phone Verify (guests) + Puja Address
 * Step 3: Coupon + Payment + Razorpay
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import toast from "react-hot-toast";
import {
  add, eachDayOfInterval, endOfMonth, endOfWeek,
  format, getDay, isAfter, isBefore, isEqual,
  isSameMonth, isThisMonth, isToday, parse,
  startOfToday, startOfWeek, addDays,
} from "date-fns";
import {
  ChevronLeft, ChevronRight, X, Check, MapPin,
  Tag, CreditCard, Shield, Calendar, Clock,
  Plus, Trash2, Phone, MessageCircle, Sunrise, Sun, Moon,
} from "lucide-react";

import {
  applyCouponCodeToCartItem, createPaymentOrder,
  getUserAddressApi, getUserDetails, removeAddress,
  removeCouponCodeToCartItem, sendPhoneOTP, updateAddressAPI,
  verifyPayment, verifyPhoneOTP, validateCouponCode, updatePoojaUserDetails
} from "@/pages/api";
import { BACKEND_API_ROUTE, POOJA_ADDRESS_API, POOJA_CART_API, POOJA_AVAILABLE_SLOTS_API } from "@/commons/api_routes";
import { GETREQUESTAPI, GETREQUESTAPI_AUTHENTICATED, PATCHREQUESTAPI, POSTREQUESTAPI } from "@/pages/api/apiUtils";
import { TIME_SLOT_OPTIONS } from "@/commons/constants";
import { resolveSlotHighlightIcon } from "@/commons/slotHighlightConfig";
import { displayRazorpay, formatDateToDDMMYYYY, getCookieItem, setCookieItem } from "@/utils/utils";
import BookingModal from "@/components/bookingComponent/BookingModal";
import AddressFlowModal from "@/components/AddressFields/AddressFlowModal";
import AddressListing from "@/components/AddressFields/AddressListing";

const E_POOJA_LOC_ID = "67eabe7700d779b9cf799b27";
const ADMIN_PHONE = "919869929292";

// ── Contact Admin helper ──────────────────────────────────────────────────────
const ContactAdmin = () => (
  <a
    href={`tel:+${ADMIN_PHONE}`}
    className="flex items-center justify-center gap-1.5 text-xs text-gray-400 hover:text-[#7B1520] transition-colors mt-1"
  >
    <Phone size={11} />
    Need help? Call us
  </a>
);

// ─────────────────────────────────────────────────────────────────────────────
export default function InlineCheckout({
  bookingPackage,
  poojaData,
  selectedLocation,
  selectedLanguage,
  selectedLocationId,
  selectedLanguageId,
  onClose,
}) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // ── Step 1 ──
  const [bmpDate, setBmpDate] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(format(startOfToday(), "MMM-yyyy"));
  const [timeslot, setTimeslot] = useState(TIME_SLOT_OPTIONS[2].value);
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState("");
  const [showAllInclusions, setShowAllInclusions] = useState(false);
  const [timeslotTouched, setTimeslotTouched] = useState(false);
  const timeslotRef = useRef(null);
  const [slotAvailability, setSlotAvailability] = useState({ restricted: false, availableSlots: [] });
  // FE-only decorative config (commons/slotHighlightConfig.js) — has no
  // bearing on which slots are actually bookable, only on how the still-
  // available ones are visually called out for configured pooja/location/language.
  const slotHighlightIcon = resolveSlotHighlightIcon(poojaData?.name, selectedLocation, selectedLanguage);

  // ── Cart ──
  const [cart, setCart] = useState(null);
  const [isEpooja, setIsEpooja] = useState(false);

  // ── Step 2 ──
  const [userData, setUserData] = useState({});
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [pickedAddress, setPickedAddress] = useState(null);
  const [showAddrFlow, setShowAddrFlow] = useState(false);
  const [showAddrList, setShowAddrList] = useState(false);

  // ── Step 3 ──
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [payType, setPayType] = useState("full");
  const [extraNotes, setExtraNotes] = useState("");
  const [orderId, setOrderId] = useState(null);
  const [bookingDone, setBookingDone] = useState(false);

  const today = startOfToday();
  const firstDay = parse(currentMonth, "MMM-yyyy", new Date());
  const days = useMemo(() =>
    eachDayOfInterval({
      start: startOfWeek(firstDay, { weekStartsOn: 0 }),
      end: endOfWeek(endOfMonth(firstDay), { weekStartsOn: 0 }),
    }), [currentMonth]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const fetchCart = useCallback(async (cartId) => {
    const token = getCookieItem("accessToken");
    const res = await GETREQUESTAPI_AUTHENTICATED(`${BACKEND_API_ROUTE}${POOJA_CART_API}/${cartId}`, token);
    if (res?.data?.statusCode === 200 && res.data.body?.length) {
      const c = res.data.body[0];
      setCart(c);
      setIsEpooja(c.location === E_POOJA_LOC_ID);
      return c;
    }
    return null;
  }, []);

  const fetchUser = useCallback(async () => {
    const res = await getUserDetails();
    if (res?.data?.statusCode === 200) {
      const u = res.data.body;
      setUserData(u);
      setPhoneVerified(!!(u?.phone && u?.isPhoneVerified));
      return u;
    }
  }, []);

  const fetchAddrs = useCallback(async () => {
    const res = await getUserAddressApi();
    if (res?.data?.statusCode === 200) {
      setAddresses(res.data.body ?? []);
    } else {
      setAddresses([]);
    }
  }, []);

  useEffect(() => {
    if (addresses.length > 0 && !pickedAddress) {
      setPickedAddress(addresses[0]);
      syncUserProfileWithAddress(addresses[0]);
    }
    if (addresses.length === 0 && pickedAddress) setPickedAddress(null);
  }, [addresses, pickedAddress, userData]);

  // Admin-configured per-date slot restrictions for this pooja+location+language.
  // No matching restriction on the backend means every slot stays available —
  // this only ever narrows TIME_SLOT_OPTIONS, never blocks the flow on its own.
  useEffect(() => {
    if (bmpDate || !selectedDay || !poojaData?._id) {
      setSlotAvailability({ restricted: false, availableSlots: [] });
      return;
    }
    let cancelled = false;
    const dateStr = formatDateToDDMMYYYY(selectedDay);
    // Mirror the same location/language resolution handleStep1Next uses when
    // it builds the cart payload, so what we show as available here matches
    // exactly what the backend will actually check on submit.
    const params = new URLSearchParams({
      poojaId: poojaData._id,
      locationId: getCookieItem("location") || selectedLocationId || "",
      languageId: getCookieItem("language") || selectedLanguageId || "",
      date: dateStr,
    });
    GETREQUESTAPI(`${BACKEND_API_ROUTE}${POOJA_AVAILABLE_SLOTS_API}?${params.toString()}`)
      .then((res) => {
        if (cancelled) return;
        const body = res?.data?.body;
        if (body) {
          setSlotAvailability({ restricted: !!body.restricted, availableSlots: body.availableSlots || [] });
        } else {
          setSlotAvailability({ restricted: false, availableSlots: [] });
        }
      })
      .catch(() => {
        if (!cancelled) setSlotAvailability({ restricted: false, availableSlots: [] });
      });
    return () => { cancelled = true; };
  }, [bmpDate, selectedDay, poojaData?._id, selectedLocationId, selectedLanguageId]);

  // If the selected slot becomes restricted after the check above resolves
  // (or the customer switches to a more-restricted date), clear it so they
  // have to actively pick one of the still-available slots.
  useEffect(() => {
    if (slotAvailability.restricted && timeslot && !slotAvailability.availableSlots.includes(timeslot)) {
      setTimeslot("");
      setTimeslotTouched(false);
    }
  }, [slotAvailability, timeslot]);

  // ── Step 1: Create cart ───────────────────────────────────────────────────
  const handleStep1Next = async () => {
    if (!bmpDate && !selectedDay) { toast.error("Please pick a date or let us choose one"); return; }
    setLoading(true);
    try {
      let token = getCookieItem("accessToken");
      const payload = {
        poojaId: bookingPackage.poojaId,
        packageId: bookingPackage.packageId,
        packageType: bookingPackage.packageType,
        location: getCookieItem("location") || selectedLocationId,
        language: getCookieItem("language") || selectedLanguageId,
        bmpChooseDate: bmpDate,
        ...(!bmpDate && selectedDay && { date: formatDateToDDMMYYYY(selectedDay) }),
        ...(!bmpDate && timeslot && { timeslot }),
        ...(notes.trim() && { timeSlotText: notes.trim() }),
      };

      const executeCartCall = async (t) => {
        return await POSTREQUESTAPI(`${BACKEND_API_ROUTE}${POOJA_CART_API}`, payload, t);
      };

      let res;
      try {
        res = await executeCartCall(token);
      } catch (err) {
        if (err?.response?.status === 401 || err?.status === 401) {
          const { handleGuestLoginFlow } = await import("@/utils/utils");
          const guestCreated = await handleGuestLoginFlow();
          if (guestCreated) {
            token = getCookieItem("accessToken");
            res = await executeCartCall(token);
          } else {
            throw err;
          }
        } else {
          throw err;
        }
      }

      if (res?.data?.statusCode === 200) {
        await fetchCart(res.data.body._id);
        await fetchUser();
        await fetchAddrs();
        setStep(2);
      } else {
        toast.error(res?.data?.message || "Could not create booking");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Something went wrong");
    } finally { setLoading(false); }
  };

  // ── Step 2: OTP ──────────────────────────────────────────────────────────
  const handleSendOTP = async () => {
    if (phone.length !== 10) { toast.error("Enter a valid 10-digit number"); return; }
    setLoading(true);
    const res = await sendPhoneOTP(`+91-${phone}`);
    if (res?.status === 201) { setOtpSent(true); toast.success("OTP sent!"); }
    else toast.error("Failed to send OTP");
    setLoading(false);
  };

  const handleVerifyOTP = async () => {
    setLoading(true);
    const token = getCookieItem("accessToken");
    const res = await verifyPhoneOTP(`+91-${phone}`, otp, token, cart?._id, userData?._id);
    if (res?.data?.statusCode === 200) {
      setCookieItem("accessToken", res.data.body.accessToken);
      setPhoneVerified(true);
      await fetchUser();
      await fetchAddrs();
      toast.success("Phone verified!");
    } else toast.error(res?.data?.message || "Invalid OTP");
    setLoading(false);
  };

  const syncUserProfileWithAddress = async (address) => {
    if (!address) return;
    const needsNameUpdate = !userData?.userName && address?.name;
    const needsEmailUpdate = !userData?.email && address?.email;

    if (needsNameUpdate || needsEmailUpdate) {
      const updateData = {};
      if (needsNameUpdate) updateData.userName = address.name;
      if (needsEmailUpdate) updateData.email = address.email;

      try {
        const updateRes = await updatePoojaUserDetails(updateData);
        if (updateRes?.data?.statusCode === 200) {
          await fetchUser();
        }
      } catch (err) {
        console.error("Failed to auto-update profile from address", err);
      }
    }
  };

  // ── Address ───────────────────────────────────────────────────────────────
  const handleAddAddress = async (data) => {
    const token = getCookieItem("accessToken");

    await syncUserProfileWithAddress(data);

    if (data?.editAddressId) await updateAddressAPI(data.editAddressId, data);
    else await POSTREQUESTAPI(`${BACKEND_API_ROUTE}${POOJA_ADDRESS_API}`, data, token);
    await fetchAddrs();
    setShowAddrFlow(false);
    setShowAddrList(true);
    toast.success("Address saved");
  };

  const handleSelectAddress = async (addr) => {
    if (cart?._id) {
      const token = getCookieItem("accessToken");
      await PATCHREQUESTAPI(`${BACKEND_API_ROUTE}${POOJA_CART_API}/${cart._id}`, { addressId: addr._id }, token);
    }
    await syncUserProfileWithAddress(addr);
    setPickedAddress(addr);
    setShowAddrList(false);
  };

  const handleStep2Next = () => {
    const needVerify = userData?.userType === 3 && !phoneVerified;
    if (needVerify) { toast.error("Please verify your phone first"); return; }
    if (!isEpooja && !pickedAddress) { toast.error("Please add a puja address"); return; }
    setStep(3);
  };

  // ── Coupon ────────────────────────────────────────────────────────────────
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) { toast.error("Enter a coupon code"); return; }
    setCouponLoading(true);
    try {
      const token = getCookieItem("accessToken");
      const vRes = await validateCouponCode({ couponCode: couponCode.trim() }, token);
      if (vRes?.data?.statusCode === 200) {
        const d = vRes.data.body;
        const aRes = await applyCouponCodeToCartItem(cart._id, { couponId: d._id, couponType: d.couponType, paymentMethodId: "COUPON" }, token);
        if (aRes?.data?.statusCode === 200) {
          setAppliedCoupon(d);
          await fetchCart(cart._id);
          toast.success(`${d.amount}${d.couponType === 1 ? "%" : "₹"} discount applied!`);
        } else toast.error("Could not apply coupon");
      } else toast.error("Invalid coupon code");
    } finally { setCouponLoading(false); }
  };

  const handleRemoveCoupon = async () => {
    const token = getCookieItem("accessToken");
    await removeCouponCodeToCartItem(cart._id, { couponId: appliedCoupon._id, paymentMethodId: "COUPON" }, token);
    setAppliedCoupon(null);
    setCouponCode("");
    await fetchCart(cart._id);
    toast.success("Coupon removed");
  };

  // ── Amounts ───────────────────────────────────────────────────────────────
  const amounts = useMemo(() => {
    if (!cart?.amounts) return { base: 0, discount: 0, gst: 0, total: 0 };
    const k = payType === "full" ? "fullPayment" : "partialPayment";
    const obj = cart.amounts[k] || {};
    const base = +(obj.amount || 0).toFixed(2);
    const discount = obj.discount || 0;
    const gst = +(obj.gstAmount || 0).toFixed(2);
    return { base, discount, gst, total: +(base + gst).toFixed(2) };
  }, [cart, payType]);

  // ── Place Order ───────────────────────────────────────────────────────────
  const handlePlaceOrder = async () => {
    if (!cart) return;
    setLoading(true);
    try {
      const oRes = await createPaymentOrder(undefined, cart._id, payType, amounts.total, extraNotes, pickedAddress?._id);
      if (oRes?.data?.statusCode === 200) {
        const { initiatedRazorPayOrderId, _doc } = oRes.data.body;
        setOrderId(_doc._id);
        const paid = await displayRazorpay({ amount: Math.round(amounts.total * 100), razorpayOrderId: initiatedRazorPayOrderId, userData });
        const vRes = await verifyPayment(paid.razorpay_payment_id, initiatedRazorPayOrderId, paid.razorpay_signature, cart._id, _doc._id);
        if (vRes?.data?.statusCode === 200) { toast.success("Booking confirmed! 🙏"); setBookingDone(true); }
        else toast.error("Payment verification failed. Please contact support.");
      } else toast.error(oRes?.data?.message || "Order creation failed");
    } catch (e) {
      if (e?.error?.code !== "PAYMENT_CANCELLED") toast.error("Payment failed. Please try again.");
    } finally { setLoading(false); }
  };

  if (bookingDone) return <BookingModal userData={userData} loading={false} orderId={orderId} />;

  const needsVerify = userData?.userType === 3 && !phoneVerified;

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        {/* Modal card */}
        <div className="relative bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col max-h-[95vh] sm:max-h-[88vh] overflow-hidden">

          {/* ── Header ── */}
          <div className="bg-gradient-to-r from-[#7B1520] to-[#C0392B] px-5 pt-5 pb-4 flex-shrink-0">
            {/* Mobile drag handle */}
            <div className="w-10 h-1 bg-white/30 rounded-full mx-auto mb-4 sm:hidden" />

            <div className="flex items-start justify-between mb-5">
              <div>
                <p className="text-white/70 text-[10px] font-bold tracking-widest uppercase mb-1">Secure Booking</p>
                <h2 className="text-white font-bold text-base leading-tight">{poojaData?.name}</h2>
                <p className="text-white/75 text-xs mt-0.5 capitalize">{bookingPackage?.packageType} Package</p>
              </div>
              <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-all flex-shrink-0 ml-2">
                <X size={15} />
              </button>
            </div>

            {/* Step indicators */}
            <div className="flex items-center">
              {[["1", "Schedule"], ["2", "Details"], ["3", "Payment"]].map(([n, label], i) => (
                <div key={n} className="flex items-center flex-1">
                  <div className="flex flex-col items-center gap-1 flex-shrink-0">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${step > +n ? "bg-white text-[#7B1520]"
                      : step === +n ? "bg-white text-[#7B1520] ring-2 ring-white/40 ring-offset-1 ring-offset-[#9B1F2F]"
                        : "bg-white/20 text-white/50"
                      }`}>
                      {step > +n ? <Check size={12} /> : n}
                    </div>
                    <span className={`text-[9px] font-semibold tracking-wide ${step >= +n ? "text-white" : "text-white/40"}`}>{label}</span>
                  </div>
                  {i < 2 && <div className={`flex-1 h-px mx-2 mb-4 ${step > +n ? "bg-white" : "bg-white/25"}`} />}
                </div>
              ))}
            </div>
          </div>

          {/* ── Scrollable body ── */}
          <div className="flex-1 overflow-y-auto">

            {/* ─────────────────────────────────────────
                STEP 1 — Schedule
            ───────────────────────────────────────── */}
            {step === 1 && (
              <div className="px-5 py-5 space-y-4">

                {/* BMP choose date */}
                <label className="flex items-center gap-3 p-3.5 rounded-2xl border border-amber-200 bg-amber-50 cursor-pointer select-none">
                  <div
                    onClick={() => { setBmpDate(v => !v); if (!bmpDate) setSelectedDay(null); }}
                    className={`w-5 h-5 rounded flex items-center justify-center border-2 flex-shrink-0 transition-all ${bmpDate ? "bg-[#7B1520] border-[#7B1520]" : "border-gray-300 bg-white"}`}
                  >
                    {bmpDate && <Check size={11} className="text-white" />}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-amber-900">Let us pick an auspicious date</p>
                    <p className="text-xs text-amber-600 mt-0.5">Our team will call you to confirm the muhurat</p>
                  </div>
                </label>

                {/* Calendar */}
                {!bmpDate && (
                  <div className="rounded-2xl border border-gray-100 overflow-hidden">
                    {/* Month nav */}
                    <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
                      <span className="text-sm font-bold text-gray-800">{format(firstDay, "MMMM yyyy")}</span>
                      <div className="flex gap-1">
                        <button onClick={() => setCurrentMonth(format(add(firstDay, { months: -1 }), "MMM-yyyy"))} disabled={isThisMonth(firstDay)} className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-200 disabled:opacity-30 transition-all">
                          <ChevronLeft size={15} />
                        </button>
                        <button onClick={() => setCurrentMonth(format(add(firstDay, { months: 1 }), "MMM-yyyy"))} className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-all">
                          <ChevronRight size={15} />
                        </button>
                      </div>
                    </div>

                    {/* Day names */}
                    <div className="grid grid-cols-7 px-2 pt-2 bg-white">
                      {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(d => (
                        <div key={d} className={`text-center text-[10px] font-bold pb-1 ${d === "Su" ? "text-rose-400" : "text-gray-400"}`}>{d}</div>
                      ))}
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-7 px-2 pb-3 gap-y-1 bg-white">
                      {days.map((day, idx) => {
                        const past = isBefore(day, today);
                        const selected = selectedDay && isEqual(day, selectedDay);
                        const inMonth = isSameMonth(day, firstDay);
                        const todayDay = isToday(day);
                        const soon = isAfter(day, today) && isBefore(day, addDays(today, 3));
                        if (!inMonth) return <div key={idx} />;
                        return (
                          <button key={idx} disabled={past}
                            onClick={() => {
                              setSelectedDay(selected ? null : day);
                              setTimeslotTouched(false); // Reset when date changes
                            }}
                            className={`h-9 w-full flex items-center justify-center rounded-xl text-xs font-medium transition-all
                              ${past ? "text-gray-300 cursor-not-allowed" : ""}
                              ${selected ? "bg-[#7B1520] text-white font-bold shadow-md scale-105" : ""}
                              ${todayDay && !selected ? "bg-rose-50 text-[#7B1520] border border-[#7B1520]/30" : ""}
                              ${soon && !selected && !todayDay ? "text-amber-600" : ""}
                              ${!selected && !todayDay && !soon && !past ? "text-gray-700 hover:bg-gray-100" : ""}
                            `}
                          >
                            {format(day, "d")}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Selected date confirmation */}
                {(selectedDay || bmpDate) && (
                  <div className="flex items-center gap-2 px-3 py-2.5 bg-green-50 rounded-xl border border-green-200 text-green-700 text-xs font-semibold">
                    <Check size={13} className="flex-shrink-0" />
                    {bmpDate ? "Our team will confirm the date" : format(selectedDay, "EEEE, d MMMM yyyy")}
                  </div>
                )}

                {!bmpDate && (
                  <div ref={timeslotRef} className="space-y-4">
                    <label className="text-xs font-bold text-gray-600 flex items-center gap-1.5 mb-1">
                      <Clock size={12} /> Preferred Time Slot
                    </label>

                    {slotAvailability.restricted && slotAvailability.availableSlots.length === 0 && (
                      <div className="px-3 py-2.5 bg-rose-50 rounded-xl border border-rose-200 text-rose-700 text-xs font-semibold">
                        This date is fully booked for the selected pooja. Please choose another date.
                      </div>
                    )}

                    {[
                      { label: "Morning", icon: <Sunrise size={13} className="text-orange-400" />, slots: TIME_SLOT_OPTIONS.filter(s => s.value.startsWith("Morning")) },
                      { label: "Afternoon", icon: <Sun size={13} className="text-amber-500" />, slots: TIME_SLOT_OPTIONS.filter(s => s.value.startsWith("Afternoon")) },
                      { label: "Evening", icon: <Moon size={13} className="text-indigo-400" />, slots: TIME_SLOT_OPTIONS.filter(s => s.value.startsWith("Evening")) },
                    ].map((group) => group.slots.length > 0 && (
                      <div key={group.label} className="space-y-2">
                        <div className="flex items-center gap-1.5 px-1">
                          {group.icon}
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{group.label}</span>
                        </div>
                        <div className="grid grid-cols-2 xs:grid-cols-3 gap-2">
                          {group.slots.map((opt, i) => {
                            const isSelected = timeslot === opt.value;
                            const isDisabled = slotAvailability.restricted && !slotAvailability.availableSlots.includes(opt.value);
                            // Only ever true for a slot that is actually still bookable — the
                            // icon/glow calls attention to it, it never overrides availability.
                            const isHighlighted = slotHighlightIcon && slotAvailability.restricted && !isDisabled;
                            return (
                              <button
                                key={i}
                                disabled={isDisabled}
                                onClick={() => { if (!isDisabled) { setTimeslot(opt.value); setTimeslotTouched(true); } }}
                                title={isDisabled ? "Not available for the selected date" : undefined}
                                className={`relative px-3 py-2.5 rounded-xl text-[11px] font-semibold transition-all border text-center
                                  ${isDisabled
                                    ? "bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed line-through"
                                    : isSelected
                                      ? "bg-[#7B1520] border-[#7B1520] text-white shadow-sm scale-[1.02]"
                                      : isHighlighted
                                        ? "bg-amber-50 border-amber-400 text-amber-800 ring-2 ring-amber-300 shadow-[0_0_10px_rgba(245,158,11,0.45)] hover:bg-amber-100"
                                        : "bg-white border-gray-100 text-gray-600 hover:border-gray-200 hover:bg-gray-50"
                                  }`}
                              >
                                {isHighlighted && (
                                  <img src={slotHighlightIcon} alt="" className="inline-block w-3.5 h-3.5 mr-1 -mt-0.5 align-middle" />
                                )}
                                {opt.option.replace(`${group.label} `, "").replace("(", "").replace(")", "")}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Special instructions — expandable */}
                {!showNotes ? (
                  <button
                    onClick={() => setShowNotes(true)}
                    className="text-xs text-[#7B1520] font-semibold flex items-center gap-1 hover:underline"
                  >
                    <Plus size={12} /> Add special instructions (muhurat / nakshatra)
                  </button>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-bold text-gray-600">Special Instructions</label>
                      <button onClick={() => { setShowNotes(false); setNotes(""); }} className="text-[10px] text-gray-400 hover:text-gray-600">Remove</button>
                    </div>
                    <textarea
                      autoFocus
                      value={notes}
                      onChange={e => setNotes(e.target.value)}
                      rows={2}
                      placeholder="e.g. Rashi, Nakshatra, or a specific muhurat preference..."
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7B1520]/20 focus:border-[#7B1520] bg-white resize-none placeholder:text-gray-400"
                    />
                  </div>
                )}
              </div>
            )}

            {/* ─────────────────────────────────────────
                STEP 2 — Details
            ───────────────────────────────────────── */}
            {step === 2 && (
              <div className="px-5 py-5 space-y-5">

                {/* Phone verification */}
                {needsVerify ? (
                  <div className="rounded-2xl bg-rose-50 border border-[#7B1520]/15 p-4 space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-[#7B1520]/10 flex items-center justify-center flex-shrink-0">
                        <Shield size={16} className="text-[#7B1520]" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-800">Verify your phone</p>
                        <p className="text-xs text-gray-500 mt-0.5">One-time verification for secure booking</p>
                      </div>
                    </div>

                    {!otpSent ? (
                      <div className="flex gap-2">
                        <div className="flex items-center flex-1 border border-gray-200 rounded-xl overflow-hidden bg-white">
                          <span className="px-3 py-3 text-sm font-bold text-gray-500 border-r border-gray-100">+91</span>
                          <input type="tel" maxLength={10} value={phone}
                            onChange={e => setPhone(e.target.value.replace(/\D/g, ""))}
                            placeholder="10-digit mobile" className="flex-1 px-3 py-3 text-sm focus:outline-none" />
                        </div>
                        <button onClick={handleSendOTP} disabled={loading || phone.length !== 10}
                          className="px-4 py-3 bg-[#7B1520] hover:bg-[#9B1F2F] text-white text-sm font-bold rounded-xl disabled:opacity-40 transition-all whitespace-nowrap flex-shrink-0">
                          {loading ? "..." : "Send OTP"}
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-xs text-gray-500 flex items-center gap-1.5">
                          <Check size={11} className="text-green-600" /> OTP sent to +91-{phone}
                          <button onClick={() => setOtpSent(false)} className="text-[#7B1520] font-semibold underline ml-1">Change</button>
                        </p>
                        <input type="tel" maxLength={6} value={otp}
                          onChange={e => setOtp(e.target.value.replace(/\D/g, ""))}
                          placeholder="Enter OTP"
                          className="w-full border-2 border-[#7B1520]/30 focus:border-[#7B1520] rounded-xl px-4 py-3.5 text-2xl font-black tracking-[0.6em] text-center text-[#7B1520] focus:outline-none bg-white transition-all" />
                        <button onClick={handleVerifyOTP} disabled={loading || otp.length < 4}
                          className="w-full py-3.5 bg-[#7B1520] hover:bg-[#9B1F2F] text-white font-bold text-sm rounded-xl disabled:opacity-40 transition-all">
                          {loading ? "Verifying..." : "Verify & Continue"}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 px-3 py-2.5 bg-green-50 rounded-xl border border-green-200">
                    <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                      <Check size={11} className="text-white" />
                    </div>
                    <p className="text-xs font-semibold text-green-700">Phone verified — {userData?.phone}</p>
                  </div>
                )}

                {/* Address */}
                {!needsVerify && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
                        <MapPin size={14} className="text-[#7B1520]" />
                        {isEpooja ? "Billing Address" : "Puja Address"}
                      </p>
                      <button
                        onClick={() => addresses.length ? setShowAddrList(true) : setShowAddrFlow(true)}
                        className="text-xs text-[#7B1520] font-bold border border-[#7B1520]/30 px-3 py-1.5 rounded-full hover:bg-rose-50 transition-all"
                      >
                        {pickedAddress ? "Change" : "+ Add"}
                      </button>
                    </div>

                    {pickedAddress ? (
                      <div className="rounded-2xl border-2 border-[#7B1520]/20 bg-white p-4">
                        <p className="font-bold text-sm text-gray-800">{pickedAddress.name}</p>
                        <p className="text-xs text-gray-500 mt-1.5 leading-5">
                          {pickedAddress.areaLocation}
                          {pickedAddress.landmark ? `, ${pickedAddress.landmark}` : ""}<br />
                          {pickedAddress.city}, {pickedAddress.state} — {pickedAddress.pincode}<br />
                          📞 {pickedAddress.phoneNumber}
                        </p>
                      </div>
                    ) : (
                      <button onClick={() => setShowAddrFlow(true)}
                        className="w-full py-4 rounded-2xl border-2 border-dashed border-[#7B1520]/30 text-[#7B1520] text-sm font-bold hover:bg-rose-50 transition-all flex items-center justify-center gap-2">
                        <Plus size={15} /> Add {isEpooja ? "Billing" : "Puja"} Address
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ─────────────────────────────────────────
                STEP 3 — Payment
            ───────────────────────────────────────── */}
            {step === 3 && (
              <div className="px-5 py-5 space-y-4">

                {/* Booking recap */}
                <div className="rounded-2xl bg-gradient-to-br from-rose-50 to-orange-50 border border-rose-100 p-4 space-y-2">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Booking Summary</p>
                  <p className="font-bold text-sm text-gray-800">{poojaData?.name}</p>
                  <div className="flex flex-wrap gap-3 text-xs text-gray-500 mt-1">
                    {!bmpDate && selectedDay && (
                      <span className="flex items-center gap-1"><Calendar size={10} /> {format(selectedDay, "d MMM yyyy")}</span>
                    )}
                    {!bmpDate && <span className="flex items-center gap-1"><Clock size={10} /> {timeslot}</span>}
                    {bmpDate && <span className="text-amber-600 font-semibold">Date to be confirmed</span>}
                  </div>

                  {/* Package specifics — Pandits & Days */}
                  {(bookingPackage?.packageData?.numberOfPandits || bookingPackage?.packageData?.numberOfDays) && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-gray-500 mt-2 pt-2 border-t border-rose-100/50">
                      {bookingPackage?.packageData?.numberOfPandits && (
                        <span className="flex items-center gap-1.5">
                          <span className="w-1 h-1 rounded-full bg-rose-400" />
                          <span className="font-bold text-gray-700">{bookingPackage.packageData.numberOfPandits}</span> {bookingPackage.packageData.numberOfPandits > 1 ? "Pandits" : "Pandit"}
                        </span>
                      )}
                      {bookingPackage?.packageData?.numberOfDays && (
                        <span className="flex items-center gap-1.5">
                          <span className="w-1 h-1 rounded-full bg-rose-400" />
                          <span className="font-bold text-gray-700">{bookingPackage.packageData.numberOfDays}</span> {bookingPackage.packageData.numberOfDays > 1 ? "Days" : "Day"}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Inclusions chips */}
                  {bookingPackage?.packageData?.inclusions?.some(i => i.present !== false) && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {bookingPackage.packageData.inclusions
                        .filter(i => i.present !== false)
                        .slice(0, showAllInclusions ? undefined : 4)
                        .map((inc, idx) => (
                          <span key={idx} className="bg-white/80 px-1.5 py-0.5 rounded-md text-[9px] text-gray-600 border border-rose-100/60 flex items-center gap-1">
                            <Check size={8} className="text-green-500" /> {inc.name}
                          </span>
                        ))
                      }
                      {bookingPackage.packageData.inclusions.filter(i => i.present !== false).length > 4 && (
                        <button 
                          onClick={() => setShowAllInclusions(!showAllInclusions)}
                          className="text-[9px] text-rose-500 font-medium self-center pl-0.5 hover:underline"
                        >
                          {showAllInclusions 
                            ? "show less" 
                            : `+${bookingPackage.packageData.inclusions.filter(i => i.present !== false).length - 4} more`
                          }
                        </button>
                      )}
                    </div>
                  )}

                  {pickedAddress && (
                    <p className="text-xs text-gray-500 flex items-center gap-1 mt-2.5">
                      <MapPin size={10} /> {pickedAddress.areaLocation}, {pickedAddress.city}
                    </p>
                  )}
                </div>

                {/* Payment type */}
                {!isEpooja && cart?.amounts?.isPartialPaymentAllowed !== false && (
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      ["full", "Full Payment", "Pay everything now"],
                      [
                        "partial",
                        "Partial Pay",
                        `Pay only ₹${+((cart?.amounts?.partialPayment?.amount || 0) + (cart?.amounts?.partialPayment?.gstAmount || 0)).toFixed(2)} now. Remaining Payment after the puja completion`
                      ]
                    ].map(([val, title, sub]) => (
                      <button key={val}
                        onClick={() => {
                          setPayType(val);
                          if (val === "partial" && appliedCoupon) {
                            handleRemoveCoupon();
                          }
                        }}
                        className={`p-3.5 rounded-2xl border-2 text-left transition-all ${payType === val ? "border-[#7B1520] bg-rose-50" : "border-gray-100 bg-white hover:border-gray-200"}`}>
                        <div className={`w-4 h-4 rounded-full border-2 mb-2 flex items-center justify-center ${payType === val ? "border-[#7B1520] bg-[#7B1520]" : "border-gray-300"}`}>
                          {payType === val && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                        </div>
                        <p className={`text-xs font-bold ${payType === val ? "text-[#7B1520]" : "text-gray-700"}`}>{title}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">{sub}</p>
                      </button>
                    ))}
                  </div>
                )}

                {/* Amount breakdown */}
                <div className="rounded-2xl border border-gray-100 overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 space-y-2">
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Cart Amount</span><span className="font-semibold text-gray-700">₹{amounts.base}</span>
                    </div>
                    {amounts.discount > 0 && (
                      <div className="flex justify-between text-xs text-green-600 font-semibold">
                        <span>Discount</span><span>- ₹{amounts.discount}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>GST</span><span className="font-semibold text-gray-700">₹{amounts.gst}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-100">
                    <span className="text-sm font-bold text-gray-800">Total Payable</span>
                    <span className="text-xl font-black text-[#7B1520]">₹{amounts.total}</span>
                  </div>
                </div>

                {/* Coupon */}
                {!appliedCoupon ? (
                  <div>
                    <label className="text-xs font-bold text-gray-600 flex items-center justify-between gap-1.5 mb-1.5">
                      <span className="flex items-center gap-1.5"><Tag size={11} /> Coupon Code</span>
                      <span className="text-[9px] font-normal text-rose-500/80 ">(Not applicable on partial pay)</span>
                    </label>
                    <div className="flex gap-2">
                      <input type="text" value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())}
                        disabled={payType === "partial"}
                        placeholder={payType === "partial" ? "COUPONS DISABLED" : "ENTER CODE"}
                        className={`flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm font-mono font-bold tracking-widest focus:outline-none transition-all uppercase placeholder:tracking-normal placeholder:font-normal ${payType === "partial" ? "bg-gray-50 text-gray-400 border-gray-100 cursor-not-allowed" : "bg-white focus:ring-2 focus:ring-[#7B1520]/20 focus:border-[#7B1520]"}`} />
                      <button onClick={handleApplyCoupon} disabled={couponLoading || !couponCode.trim() || payType === "partial"}
                        className="px-5 py-3 bg-[#7B1520] hover:bg-[#9B1F2F] text-white text-sm font-bold rounded-xl disabled:opacity-40 transition-all">
                        {couponLoading ? "..." : "Apply"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0"><Check size={12} className="text-white" /></div>
                      <div>
                        <p className="text-xs font-bold text-green-800">{appliedCoupon.couponCode}</p>
                        <p className="text-xs text-green-600">{appliedCoupon.amount}{appliedCoupon.couponType === 1 ? "%" : "₹"} discount applied</p>
                      </div>
                    </div>
                    <button onClick={handleRemoveCoupon} className="text-xs font-bold text-red-500 hover:text-red-700">Remove</button>
                  </div>
                )}

                {/* Extra notes */}
                <div>
                  <label className="text-xs font-bold text-gray-600 mb-1.5 block">Additional Notes <span className="font-normal text-gray-400">(optional)</span></label>
                  <textarea value={extraNotes} onChange={e => setExtraNotes(e.target.value)} rows={2}
                    placeholder="Any special instructions for the pandit..."
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7B1520]/20 focus:border-[#7B1520] bg-white resize-none" />
                </div>

                <p className="text-[10px] text-gray-400 text-center leading-4">
                  By placing this order you agree to our{" "}
                  <a href="/terms-and-conditions" className="text-[#7B1520] underline" target="_blank">T&C</a> and{" "}
                  <a href="/privacy-policy" className="text-[#7B1520] underline" target="_blank">Privacy Policy</a>
                </p>
              </div>
            )}
          </div>

          {/* ── Sticky Footer ── */}
          <div className="px-5 pt-3 pb-5 bg-white border-t border-gray-100 flex-shrink-0 space-y-2">
            {step === 1 && (
              <button
                onClick={() => {
                  if (selectedDay && !timeslotTouched && !bmpDate) {
                    timeslotRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    setTimeslotTouched(true);
                  } else {
                    handleStep1Next();
                  }
                }}
                disabled={loading || (!selectedDay && !bmpDate)}
                className="w-full py-4 rounded-2xl bg-[#7B1520] hover:bg-[#9B1F2F] disabled:opacity-40 text-white font-bold text-sm shadow-md hover:shadow-lg transition-all active:scale-[0.98]"
              >
                {loading
                  ? "Setting up your booking…"
                  : (selectedDay && !timeslotTouched && !bmpDate)
                    ? "Select Time Slot ↓"
                    : "Continue to Details →"}
              </button>
            )}

            {step === 2 && (
              <div className="flex gap-2">
                <button onClick={() => setStep(1)} className="w-11 h-12 rounded-xl border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 flex-shrink-0">
                  <ChevronLeft size={18} />
                </button>
                <button onClick={handleStep2Next} disabled={needsVerify || (!isEpooja && !pickedAddress)}
                  className="flex-1 py-3.5 rounded-2xl bg-[#7B1520] hover:bg-[#9B1F2F] disabled:opacity-40 text-white font-bold text-sm shadow-md hover:shadow-lg transition-all active:scale-[0.98]">
                  Review Payment →
                </button>
              </div>
            )}

            {step === 3 && (
              <>
                <button onClick={handlePlaceOrder} disabled={loading}
                  className="w-full py-4 rounded-2xl bg-[#7B1520] hover:bg-[#9B1F2F] disabled:opacity-40 text-white font-bold text-sm shadow-md hover:shadow-lg transition-all active:scale-[0.98] flex items-center justify-center gap-2">
                  {loading
                    ? <><svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" strokeDasharray="31 10" /></svg> Processing…</>
                    : `🙏 Place Order — ₹${amounts.total}`}
                </button>
                <button onClick={() => setStep(2)} className="w-full py-1 text-xs text-gray-400 hover:text-gray-600 transition-colors">
                  ← Back
                </button>
              </>
            )}

            <ContactAdmin />
          </div>
        </div>
      </div>

      {/* Address flow modals */}
      {showAddrFlow && (
        <AddressFlowModal userData={userData} closeAddressModal={() => setShowAddrFlow(false)} handleAddAddress={handleAddAddress} otpHandlers={() => { }} />
      )}
      {showAddrList && (
        <AddressListing addresses={addresses} fetchAddresses={fetchAddrs} handleAddAddress={handleAddAddress}
          userData={userData} otpHandlers={() => { }} onSelectAddress={handleSelectAddress}
          closeModal={() => setShowAddrList(false)} defaultAddress={pickedAddress}
          handleDeleteAddress={async (id) => { await removeAddress(id); await fetchAddrs(); }} />
      )}
    </>
  );
}
